import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES, TYRETEAM_CHANNELS } from "models";
import {
  useOrderTyreNetMutation,
  useOrderTyreTeamMutation
} from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOrderModal/hooks";
import { useTyreNetStock, useTyreTeamStock } from "modules/AppointmentDetails/components/VehicleTyres/hooks";
import { tyreReplacementsToOrders } from "modules/AppointmentDetails/components/VehicleTyres/utils";
import { delay } from "utils/common";

import type { Appointment, DealerLocation, Tyre, VehicleTyreReplacement } from "models";

type UseTyreReplacementOrderProps = {
  open: boolean;
  appointment: Appointment;
  location: DealerLocation | undefined;
  existingReplacements: VehicleTyreReplacement[];
  onClose: () => void;
};

export enum REPLACEMENT_ORDER_STATUS {
  Pending = 1,
  Fulfilled,
  Rejected
}

export interface ReplacementOrder extends VehicleTyreReplacement {
  quantity: number;
  not_ordered_yet_quantity: number;
  status?: REPLACEMENT_ORDER_STATUS;
  stock?: string;
  gross_price?: number;
  net_price?: number;
}

const ORDER_DELAY_MS = 2000;

export const useTyreReplacementOrder = ({ appointment, location, onClose, open, existingReplacements }: UseTyreReplacementOrderProps) => {
  const { t } = useTranslation();

  const [ref5, setRef5] = useState("");
  const [ref6, setRef6] = useState("");
  const [replacementOrders, setReplacementOrders] = useState<ReplacementOrder[]>([]);
  const [isOrdering, setIsOrdering] = useState(false);

  const canOrderTyreTeamReplacementTyres = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.TyreTeamCatalogOrder);
  const canOrderTyreNetReplacementTyres = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.TyreNetOrder);
  const orderTyreTeamMutation = useOrderTyreTeamMutation();
  const orderTyreNetMutation = useOrderTyreNetMutation();

  const isTyreTeamEnabled = location?.is_tyre_team_enabled ?? false;
  const isTyreNetEnabled = location?.is_tyre_net_enabled ?? false;
  const tyreTeamStock = useTyreTeamStock(isTyreTeamEnabled, replacementOrders);
  const tyreNetStock = useTyreNetStock(isTyreNetEnabled, replacementOrders);
  const { data: stock } = isTyreTeamEnabled ? tyreTeamStock : tyreNetStock;

  useEffect(() => {
    if (!stock) return;

    const findTyreStock = (order: ReplacementOrder): Tyre | undefined =>
      isTyreTeamEnabled ? stock.find(s => s.tyre_team_system_number === order.tyre_team_system_number) : stock.find(s => s.ean === order.tyre?.ean);

    setReplacementOrders(orders =>
      orders.map(order => {
        const stock = findTyreStock(order);
        return { ...order, stock: stock?.stock, net_price: stock?.net_price, gross_price: stock?.gross_price };
      })
    );
  }, [stock]);

  useEffect(() => {
    if (!replacementOrders.length) return;

    setRef5(`EAN: ${replacementOrders[0].tyre?.ean ?? ""}`);
  }, [replacementOrders]);

  useEffect(() => setReplacementOrders(open ? tyreReplacementsToOrders(existingReplacements, isTyreTeamEnabled) : []), [open]);

  const handleQuantityUpdate = (order: ReplacementOrder, quantity: number) => setReplacementOrders(orders => orders.map(o => (o === order ? { ...o, quantity } : o)));

  const handleStatusUpdate = (index: number, status: REPLACEMENT_ORDER_STATUS) => {
    if (status !== REPLACEMENT_ORDER_STATUS.Fulfilled) return setReplacementOrders(orders => orders.map((o, idx) => (index === idx ? { ...o, status } : o)));

    setReplacementOrders(orders =>
      orders.map((o, idx) =>
        index === idx
          ? {
              ...o,
              status,
              stock: !o.stock?.includes(">") ? Math.max(0, Number(o.stock) - o.quantity).toString() : o.stock,
              quantity: o.quantity - 1,
              not_ordered_yet_quantity: o.not_ordered_yet_quantity - 1
            }
          : o
      )
    );
  };

  const handlePlaceOrders = async () => {
    if ((!canOrderTyreTeamReplacementTyres && isTyreTeamEnabled) || (!canOrderTyreNetReplacementTyres && isTyreNetEnabled)) return;

    const ordersToPlace = replacementOrders.filter(item => item.quantity > 0);
    if (!ordersToPlace.length) return;

    setIsOrdering(true);
    setReplacementOrders(orders => orders.map(item => (item.quantity > 0 ? { ...item, status: REPLACEMENT_ORDER_STATUS.Pending } : item)));

    for (let i = 0; i < ordersToPlace.length; i++) {
      if (i > 0) await delay(ORDER_DELAY_MS);

      try {
        const payload = {
          appointment_id: appointment.id,
          system_number: ordersToPlace[i].tyre_team_system_number ?? "",
          quantity: ordersToPlace[i].quantity,
          delivery: ordersToPlace[i].delivery_method || "",
          channel: ordersToPlace[i].tyre_team_channel ?? TYRETEAM_CHANNELS.WheelTyre,
          ref4: location?.is_tyre_team_autofill_price_ref_on_order ? String(ordersToPlace[i].price) : "",
          ref5,
          ref6,
          ean: ordersToPlace[i].tyre?.ean ?? "",
          order_reference: appointment.wo_nr,
          order_remarks: ref6
        };

        await (isTyreTeamEnabled ? orderTyreTeamMutation.mutateAsync(payload) : orderTyreNetMutation.mutateAsync(payload));
        handleStatusUpdate(i, REPLACEMENT_ORDER_STATUS.Fulfilled);
      } catch (error: unknown) {
        handleStatusUpdate(i, REPLACEMENT_ORDER_STATUS.Rejected);
        toast.error(error instanceof Error ? error.message : t("v8_error_placing_orders", "Error placing orders"));
      }
    }

    setIsOrdering(false);

    if (replacementOrders.some(o => o.quantity > 0 && o.status !== REPLACEMENT_ORDER_STATUS.Fulfilled)) onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const sanitizedValue = value.replace(/[^a-zA-Z0-9 ]/g, "").substring(0, 30);

    if (name === "ref5") setRef5(sanitizedValue);
    else if (name === "ref6") setRef6(sanitizedValue);
  };

  return {
    replacementOrders,
    isFetchingStock: tyreTeamStock.isFetching || tyreNetStock.isFetching,
    ref5,
    ref6,
    handleInputChange,
    handleQuantityUpdate,
    handlePlaceOrders,
    isOrdering
  };
};
